import { defineStore } from 'pinia'
import { ref } from 'vue'
import { db } from '@/services/firebase'
import { ref as dbRef, get, set, update, child } from 'firebase/database'

export const useSessionStore = defineStore('session', () => {
  const sessionId = ref(null)
  const tableId = ref(null)
  const pinCode = ref(null)
  const mode = ref('single') // 'single' | 'collaborative'
  const currentUser = ref(null) // { id, name, isHost }
  const restaurantId = ref(null) // Laravel restaurant ID
  const restaurantSlug = ref(null) // URL slug

  const saveSession = () => {
    localStorage.setItem('dandero_session', JSON.stringify({
      sessionId: sessionId.value,
      tableId: tableId.value,
      pinCode: pinCode.value,
      mode: mode.value,
      currentUser: currentUser.value,
      restaurantId: restaurantId.value,
      restaurantSlug: restaurantSlug.value
    }))
  }

  const restoreSession = () => {
    const saved = localStorage.getItem('dandero_session')
    if (saved) {
      try {
        const data = JSON.parse(saved)
        sessionId.value = data.sessionId
        tableId.value = data.tableId
        pinCode.value = data.pinCode
        mode.value = data.mode
        currentUser.value = data.currentUser
        restaurantId.value = data.restaurantId
        restaurantSlug.value = data.restaurantSlug
        return true
      } catch (e) {
        console.error('Failed to restore session', e)
        localStorage.removeItem('dandero_session')
      }
    }
    return false
  }

  const startIndividualSession = async (tableNum, restId = null, restSlug = null) => {
    const newSessionId = `sess_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`
    const userId = `guest_${Date.now()}`
    
    const updates = {}
    updates[`sessions/${newSessionId}`] = {
      tableId: tableNum,
      restaurantId: restId,
      restaurantSlug: restSlug,
      type: 'individual',
      createdAt: Date.now(),
      status: 'active',
      users: {
        [userId]: {
          id: userId,
          name: 'Guest',
          isHost: true,
          joinedAt: Date.now()
        }
      }
    }
    
    await update(dbRef(db), updates)

    sessionId.value = newSessionId
    tableId.value = tableNum
    mode.value = 'single'
    pinCode.value = null
    currentUser.value = { id: userId, name: 'Guest', isHost: true }
    restaurantId.value = restId
    restaurantSlug.value = restSlug
    saveSession()
  }

  const createCollaborativeSession = async (tableNum, hostName, restId = null, restSlug = null) => {
    const newSessionId = `sess_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`
    const newPin = Math.floor(1000 + Math.random() * 9000).toString()
    const userId = `host_${Date.now()}`

    const updates = {}
    updates[`sessions/${newSessionId}`] = {
      tableId: tableNum,
      restaurantId: restId,
      restaurantSlug: restSlug,
      type: 'collaborative',
      pin: newPin,
      createdAt: Date.now(),
      status: 'active',
      users: {
        [userId]: {
          id: userId,
          name: hostName,
          isHost: true,
          joinedAt: Date.now()
        }
      }
    }
    
    // Map PIN to Session ID (scoped by restaurant for uniqueness)
    updates[`pins/${newPin}`] = newSessionId

    await update(dbRef(db), updates)

    sessionId.value = newSessionId
    tableId.value = tableNum
    pinCode.value = newPin
    mode.value = 'collaborative'
    currentUser.value = { id: userId, name: hostName, isHost: true }
    restaurantId.value = restId
    restaurantSlug.value = restSlug
    saveSession()
  }

  const joinSession = async (pin, userName, expectedRestaurantSlug = null) => {
    // Lookup Session ID from PIN
    const pinSnapshot = await get(child(dbRef(db), `pins/${pin}`))
    
    if (!pinSnapshot.exists()) {
      throw new Error('Invalid PIN code')
    }

    const foundSessionId = pinSnapshot.val()
    
    // Verify session is still active
    const sessionSnapshot = await get(child(dbRef(db), `sessions/${foundSessionId}`))
    if (!sessionSnapshot.exists() || sessionSnapshot.val().status !== 'active') {
      throw new Error('Session is no longer active')
    }
    
    const sessionData = sessionSnapshot.val()

    // Verify restaurant matches (if provided)
    if (expectedRestaurantSlug && sessionData.restaurantSlug !== expectedRestaurantSlug) {
      throw new Error('This PIN belongs to a different restaurant')
    }

    // Add user to session
    const userId = `user_${Date.now()}`
    const user = {
      id: userId,
      name: userName,
      isHost: false,
      joinedAt: Date.now()
    }

    await set(child(dbRef(db), `sessions/${foundSessionId}/users/${userId}`), user)

    sessionId.value = foundSessionId
    tableId.value = sessionData.tableId
    pinCode.value = pin
    mode.value = 'collaborative'
    currentUser.value = user
    restaurantId.value = sessionData.restaurantId
    restaurantSlug.value = sessionData.restaurantSlug
    saveSession()
  }

  const leaveSession = () => {
    sessionId.value = null
    tableId.value = null
    pinCode.value = null
    mode.value = 'single'
    currentUser.value = null
    restaurantId.value = null
    restaurantSlug.value = null
    localStorage.removeItem('dandero_session')
  }

  return {
    sessionId,
    tableId,
    pinCode,
    mode,
    currentUser,
    restaurantId,
    restaurantSlug,
    startIndividualSession,
    createCollaborativeSession,
    joinSession,
    leaveSession,
    restoreSession
  }
})
